import React, { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { U2000HandleApiError } from '../../u2000Core/axios/U2000HandleApiError';
import { useU2000QuickSearchListEffect } from '../../u2000Core/hooks/useU2000QuickSearchListEffect';
import { useToast } from '../../u2000Core/context/toast/U2000ToastContext';
import { useU2000LogicalUnitContext } from '../../u2000Core/context/logicalUnit/U2000LogicalUnitContext';
import U2000BasicTable, { type U2000SearchResult } from '../../u2000Core/components/tables/basic/U2000BasicTable';
import type { U2000CriteriaLookupConfig } from '../../u2000Core/components/tables/basic/U2000BasicTableList';
import {
  U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT,
  defaultItemToValue,
  registerCriteriaLookup,
  unregisterCriteriaLookup,
} from '../../u2000Core/components/tables/commons/search/U2000CriteriaLookupRegistry';
import { U2000GridStateEnumPaginationDirection } from '../../u2000Core/reducer/formReducer/U2000GridState';
import type { U2000TableHandlers } from '../../u2000Core/types/U2000TableHandlers';
import {
  U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION,
  u3121FormatProjectLookupLabel,
} from '../../u3121Project/U3121Service';
import { useU3121FindProjectHandlers } from '../../u3121Project/U3121UseFindHandlers';
import { useU3500Context } from '../U3500Context';
import { NS } from '../U3500';
import { luHandleAppiError, type U3500DocumentSearchItem } from '../U3500Service';
import { useU3500DocumentSearchHandlers } from '../hooks/useU3500DocumentSearchHandlers';
import { getU3500DocumentSearchColumnsDef } from './list/U3500DocumentSearchListColumns';

/* eslint-disable react-refresh/only-export-components */
export function useU3500DocumentSearchListHandlerActions<T>(handlers: U2000TableHandlers<T>): U2000TableHandlers<T> {
  return handlers;
}

function u3500ProjectLookupItemToDisplay(
  item: unknown,
  _config: U2000CriteriaLookupConfig,
  inactiveSuffix: string
): string {
  return u3121FormatProjectLookupLabel(item as Record<string, unknown>, inactiveSuffix);
}

const U3500DocumentSearchList: React.FC = () => {
  const { t } = useTranslation();
  const toast = useToast();
  const navigate = useNavigate();
  const { initLU, searchStateHandlers, gridStateHandlers } = useU3500Context();
  const { setLogicalUnitBreadcrumb, quickSearchData, setQuickSearchData } = useU2000LogicalUnitContext();
  const consumeQsSessionRef = useRef<() => { isFromQuickSearch: boolean; targetLogicalUnit: string } | null>(() => null);
  const findProjectHandlers = useU3121FindProjectHandlers();

  useEffect(() => {
    const inactiveProjectSuffix = t('valInactive', { ns: 'u3121' });
    registerCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT, {
      search: (criterias) =>
        findProjectHandlers.search([...criterias, U3121_FIND_PROJECT_INCLUDE_INACTIVE_CRITERION]),
      itemToValue: defaultItemToValue,
      itemToDisplay: (item, config) => u3500ProjectLookupItemToDisplay(item, config, inactiveProjectSuffix),
      itemToSelectedDisplay: (item, config) => u3500ProjectLookupItemToDisplay(item, config, inactiveProjectSuffix),
    });
    return () => {
      unregisterCriteriaLookup(U2000_CRITERIA_LOOKUP_U3121_FIND_PROJECT);
    };
  }, [findProjectHandlers.search, t]);

  useEffect(() => {
    searchStateHandlers.setColumns(getU3500DocumentSearchColumnsDef(t));
    gridStateHandlers.setColumns(getU3500DocumentSearchColumnsDef(t));
    setLogicalUnitBreadcrumb([{ label: t('title', { ns: NS }) }]);
  }, [t]); // eslint-disable-line react-hooks/exhaustive-deps -- aligné autres listes UL

  const handleSearch = useU3500DocumentSearchHandlers({
    onSuccess: (searchResult: U2000SearchResult<U3500DocumentSearchItem>, direction: U2000GridStateEnumPaginationDirection) => {
      gridStateHandlers.initGridAfterSearch(searchResult, direction);
      searchStateHandlers.keepFilterAfterSearch(searchStateHandlers.extractSearchParameter());
      void consumeQsSessionRef.current();
    },
    onError: ({ error }: { error: unknown }) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- aligné sur les autres UL
      const errorResult = U2000HandleApiError(error as any, t, NS, luHandleAppiError);
      if (errorResult.genericErrors?.length) {
        errorResult.genericErrors.forEach((err) => toast.error(err.text, err.title));
      }
    },
  });

  const u3500ListHandlerActions: U2000TableHandlers<U3500DocumentSearchItem> = useU3500DocumentSearchListHandlerActions({
    onSearch: (page: number, direction: U2000GridStateEnumPaginationDirection) => {
      handleSearch.handleSearch(
        searchStateHandlers.extractSearchParameter(),
        gridStateHandlers.extractPagination(page),
        direction
      );
    },
    onRowClick: (row: U3500DocumentSearchItem) => {
      if (row.projectId) {
        navigate(`/U3121/${row.projectId}`);
        return;
      }
      toast.info(t('msgOrphanNoProject', { ns: NS }), t('infoTitle', { ns: NS }));
    },
    onAdd: () => {},
    onModify: () => {},
    onDelete: () => {},
    onExportExcel: async () => {},
  });

  const { consumeQuickSearchSession } = useU2000QuickSearchListEffect({
    targetLogicalUnit: '/U3500',
    t,
    searchStateHandlers,
    gridStateHandlers,
    runSearch: handleSearch.handleSearch,
    quickSearchData,
    setQuickSearchData,
  });
  consumeQsSessionRef.current = consumeQuickSearchSession;

  if (searchStateHandlers.getColumns().length === 0) {
    return null;
  }

  return (
    <U2000BasicTable<U3500DocumentSearchItem>
      searchStateHandlers={searchStateHandlers}
      gridStateHandlers={gridStateHandlers}
      label={t('lblDocumentHit', { count: gridStateHandlers.getTotalRows(), ns: NS })}
      handlerActions={u3500ListHandlerActions}
      allowRowSelection={false}
      hideSearchPanel={initLU.hideSearch}
    />
  );
};

export default U3500DocumentSearchList;
